import React from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState, StatCard } from "@/components/common";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { TrendingUp, Target, FileText, ThumbsUp, AlertTriangle } from "lucide-react";

export default function Performance() {
  const { data, isLoading } = useQuery({ queryKey: ["performance"], queryFn: () => get("/performance") });
  if (isLoading || !data) return <PageLoader />;
  if (data.total_tests === 0) return <div><PageHeader title="Performance" /><EmptyState icon={FileText} title="No data yet" hint="Attempt a mock test to unlock your analytics." /></div>;
  const radar = data.subject_analysis.map((s) => ({ subject: s.subject_id.slice(0, 8), accuracy: s.accuracy }));

  return (
    <div>
      <PageHeader title="Performance Analytics" subtitle="Track progress, spot weak areas, improve faster" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard icon={FileText} label="Tests Taken" value={data.total_tests} tone="indigo" />
        <StatCard icon={Target} label="Overall Accuracy" value={`${data.overall_accuracy}%`} tone="amber" />
        <StatCard icon={TrendingUp} label="Subjects Tracked" value={data.subject_analysis.length} tone="jade" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-bold text-slate-900 mb-4">Score Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend}><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Line type="monotone" dataKey="score" stroke="#1E3A8A" strokeWidth={3} dot={{ r: 3 }} /><Line type="monotone" dataKey="accuracy" stroke="#0F766E" strokeWidth={2} /></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-bold text-slate-900 mb-4">Subject Accuracy Radar</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={radar}><PolarGrid /><PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} /><Radar dataKey="accuracy" stroke="#7C3AED" fill="#7C3AED" fillOpacity={0.4} /><Tooltip /></RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-5 mt-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-bold text-emerald-700 flex items-center gap-2 mb-3"><ThumbsUp className="h-5 w-5" /> Strength Areas</h3>
          {data.strengths.map((s) => <Row key={s.subject_id} name={s.subject_id} acc={s.accuracy} tone="emerald" />)}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-display font-bold text-red-600 flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5" /> Weak Areas</h3>
          {data.weak_areas.map((s) => <Row key={s.subject_id} name={s.subject_id} acc={s.accuracy} tone="red" />)}
        </div>
      </div>
    </div>
  );
}
function Row({ name, acc, tone }) {
  return <div className="flex items-center gap-3 py-1.5"><span className="text-sm text-slate-700 flex-1 truncate">{name}</span><div className="w-28 h-2 bg-slate-100 rounded-full"><div className={`h-2 rounded-full ${tone === "emerald" ? "bg-emerald-500" : "bg-red-500"}`} style={{ width: `${acc}%` }} /></div><span className="text-sm font-bold w-12 text-right">{acc}%</span></div>;
}
